/** Renders persisted custom report tabs against one or more submitted forms. */
import { useEffect, useMemo, useState } from 'react';
import { getFormsDataList } from '../../api/forms-api';
import { getEditableReport } from '../../api/reports-api';
import { useUiLocale } from '../../app/ui-locale';
import { ErrorMessage, Loading } from '../../components/Loading';
import type { FormValues } from '../../models/form';
import type { DynamicReport } from '../../models/report';
import { sanitizeCustomHtml } from '../../utils/customization';
import { publicCopy } from '../renderer/public-copy';

function readPath(source: unknown, path: string): unknown {
  let current = source;
  for (const part of path.split('.').filter(Boolean)) {
    if (!current || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}


function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function renderValue(value: unknown): string {
  if (!Array.isArray(value)) return escapeHtml(value);
  return value.map((item) => {
    if (!item || typeof item !== 'object') return escapeHtml(item);
    return Object.values(item as Record<string, unknown>).map(escapeHtml).join(' | ');
  }).join('<br>');
}

function renderTemplate(template: string, data: FormValues): string {
  const cleaned = template
    .replace(/\sng-[a-z-]+=("[^"]*"|'[^']*')/gi, '')
    .replace(/\sdir-table-controller(?:=("[^"]*"|'[^']*'))?/gi, '');
  const rendered = cleaned.replace(/{{\s*(?:form\.|DynamicForm\d+\.)?([^}|]+)(?:\|\s*[^}]+)?\s*}}/g, (_match, rawPath: string) =>
    renderValue(readPath(data, rawPath.trim()))
  );
  return sanitizeCustomHtml(rendered);
}

export function CustomReportApp({ reportId }: { reportId: number }) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const [report, setReport] = useState<DynamicReport>();
  const [items, setItems] = useState<FormValues[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    const ids = (localStorage.getItem('formIDsToReport') ?? '').split(',').map(Number).filter((id) => id > 0);
    Promise.all([getEditableReport(reportId), ids.length ? getFormsDataList(ids) : Promise.resolve([])])
      .then(([loadedReport, loadedItems]) => { setReport(loadedReport); setItems(loadedItems); })
      .catch(setError).finally(() => setLoading(false));
  }, [reportId]);
  const header = useMemo(() => report?.Tabs?.find((tab) => tab.Name.toLowerCase().includes('header')) ?? report?.Tabs?.[0], [report]);
  const body = useMemo(() => report?.Tabs?.find((tab) => tab.Name.toLowerCase().includes('body')) ?? report?.Tabs?.[1], [report]);
  const footer = useMemo(() => report?.Tabs?.find((tab) => tab.Name.toLowerCase().includes('footer')) ?? report?.Tabs?.[2], [report]);
  if (loading) return <Loading />;
  if (error) return <ErrorMessage error={error} />;
  if (!report) return <div className="alert alert-warning">{copy.reportNotFound}</div>;
  return <section className="df-custom-report"><header className="df-page-header no-print"><div><h2>{report.Title}</h2><p>{report.Description}</p></div><button type="button" className="btn btn-default" onClick={() => window.print()}>{copy.print}</button></header>{header ? <div className="df-custom-report__header" dangerouslySetInnerHTML={{ __html: renderTemplate(header.Body, items[0] ?? {}) }} /> : null}<div className="df-custom-report__items">{items.map((item, index) => <article className="df-custom-report__item" key={index} dangerouslySetInnerHTML={{ __html: renderTemplate(body?.Body ?? '', item) }} />)}{!items.length ? <div className="alert alert-info">{copy.noReportRecords}</div> : null}</div>{footer ? <div className="df-custom-report__footer" dangerouslySetInnerHTML={{ __html: renderTemplate(footer.Body, items[0] ?? {}) }} /> : null}</section>;
}
